import React, { useState } from "react";
import { IonButton, IonIcon, IonSpinner, IonToast } from "@ionic/react";
import { closeCircleOutline, mailOutline } from "ionicons/icons";
import {
  MyInvitation,
  declinedRequestSummary,
  invitationSummary,
  relationshipCards,
  acceptReplacesCurrentOrg,
  replacementWarning,
} from "../../../services/orgRelationship";
import { UseMyOrgRelationship, useMyOrgRelationship } from "./useMyOrgRelationship";
import AcceptInvitationConfirm from "./AcceptInvitationConfirm";
import "./ShareOrgs.css";

/**
 * The patient's HME relationship, at the top of the Data Sharing screen.
 *
 * ⭐ `association` ("who holds my data") and `invitation` ("who is asking" → Accept/Decline) are
 * separate fields on `GET /me` and CAN BE SET AT THE SAME TIME: an organization that typed the
 * patient in already holds their record and may still be asking them to confirm it.
 *
 * ⛔ F-523 (UAT, URS_5.5, preview-101) — the tester had the "Your equipment provider" card
 * (the `association` display) removed from this screen entirely; only the `invitation` card
 * still renders here. The X on "Who can see my data" (`useOrgRemoval`, via `leavableOrgs`) is
 * unaffected — it still reads `association` to synthesize the leave row for an org-created link
 * that the share directory never lists, it is only this component's own display of it that is
 * gone.
 *
 * ⛔ F-492 (UAT, URS_6.8, preview-90) — the patient has only ONE way to leave an organization.
 * The tester: "We need to give the patient only one way to leave the organization. If we provide
 * them with the X, then when the user clicks it, they leave the organization. There shouldn't be
 * two options." The X on the organization's row in "Who can see my data" is that one way
 * (`useOrgRemoval` → the same `leave`), sharing THIS hook instance via `MyOrgRelationshipCardsView`.
 */
type Props = {
  /**
   * Called after any change, so the org directory ("Who can see my data") refetches too.
   *
   * The resolved value is deliberately `unknown`. This prop is wired to `useOrgSharing`'s
   * `reload`, which resolves to the reloaded `ShareableOrg[]` for its own callers; we only
   * await it. Narrowing to `Promise<void>` would reject the very callback this prop exists for.
   */
  onChanged?: () => void | Promise<unknown>;
};

type ViewProps = Props & {
  /** The ONE relationship hook instance the screen shares (the X on "Who can see my data" leaves
   *  through it), so leaving updates this card without a second `GET /me`. */
  rel: UseMyOrgRelationship;
};

export const MyOrgRelationshipCardsView: React.FC<ViewProps> = ({ rel, onChanged }) => {
  // R-32: set only when accepting would END another organization's access. Null is the
  // ordinary case, and the ordinary case is still one tap.
  const [confirmingAccept, setConfirmingAccept] = useState<MyInvitation | null>(null);
  // F-523 (UAT, URS_5.5, preview-101): the tester had the "Your equipment provider" card removed
  // from the Data Sharing screen entirely — `showAssociation` is no longer used to render
  // anything here. The X on "Who can see my data" (`useOrgRemoval`, via `leavableOrgs`) remains
  // the patient's one way to leave the organization; only this card's own display is gone.
  const { showInvitation } = relationshipCards(rel);
  // Bind once: the closures below outlive JSX narrowing, and a non-null assertion there would
  // be a lie the moment a reload lands between render and tap.
  const association = rel.association;
  const invitation = rel.invitation;
  // F-600 (URS_6.9 AC5) — a request the patient made that an organization declined. Shown here,
  // on the screen the request was made from; the kernel clears it when the patient asks again.
  const declined = rel.declinedShareRequest ?? null;

  const after = async (ok: boolean) => {
    if (ok) await onChanged?.();
  };

  const acceptInvitation = async (inv: MyInvitation) => await after(await rel.accept(inv.org_id));

  /**
   * ⛔ The act that only REVOKES (Leave) was guarded and the act that revokes AND GRANTS was
   * not. Accepting an invitation from a different organization ends the incumbent's access, so
   * it is disclosed first — and only then, because a dialog in front of every accept is noise.
   */
  const onAcceptTapped = async (inv: MyInvitation) => {
    if (acceptReplacesCurrentOrg(rel)) {
      setConfirmingAccept(inv);
      return;
    }
    await acceptInvitation(inv);
  };

  // Nothing to say while loading, and nothing to say when there is no invitation to show — the
  // association no longer renders a card of its own (F-523), and the directory below is the
  // whole screen for a patient with neither.
  if (rel.loading || (!showInvitation && !declined)) {
    return (
      <IonToast
        isOpen={!!rel.toast}
        message={rel.toast}
        duration={2500}
        onDidDismiss={() => rel.setToast("")}
      />
    );
  }

  return (
    <div id="my-hme-section" className="mt-4">
      {declined && (
        <div className="care-org-card" data-testid="declined-share-request">
          <div className="care-org-section-label">
            <IonIcon icon={closeCircleOutline} color="medium" />
            <span>Request declined</span>
          </div>
          <p className="care-org-sub">{declinedRequestSummary(declined)}</p>
          {declined.reason?.trim() && <p className="care-org-sub">Reason: {declined.reason.trim()}</p>}
        </div>
      )}

      {showInvitation && invitation && (
        <div className="care-org-card">
          <div className="care-org-section-label">
            <IonIcon icon={mailOutline} color="primary" />
            <span>Invitation</span>
          </div>
          {/* ⭐ One shared string with the home dashboard's card (B2-4 Task B). Two copies of
              this sentence would drift, and the patient would be told two different things
              about one invitation. */}
          <p className="care-org-sub">{invitationSummary(invitation)}</p>
          <IonButton
            className="care-org-btn care-org-btn-primary"
            expand="block"
            disabled={rel.busyOrgId === invitation.org_id}
            onClick={async () => await onAcceptTapped(invitation)}
          >
            {rel.busyOrgId === invitation.org_id ? <IonSpinner name="dots" /> : "Accept"}
          </IonButton>
          <IonButton
            className="care-org-btn care-org-btn-outline"
            fill="outline"
            expand="block"
            disabled={rel.busyOrgId === invitation.org_id}
            onClick={async () => await after(await rel.decline(invitation.org_id))}
          >
            Decline
          </IonButton>
        </div>
      )}

      {/* ⛔ The card's own sentence above STAYS — this is appended to the flow, never
          substituted for it. It answers a different question: what the INCUMBENT loses. */}
      <AcceptInvitationConfirm
        warning={replacementWarning({ association, invitation: confirmingAccept })}
        busy={!!confirmingAccept && rel.busyOrgId === confirmingAccept.org_id}
        onConfirm={async () => {
          const inv = confirmingAccept;
          setConfirmingAccept(null);
          if (inv) await acceptInvitation(inv);
        }}
        onCancel={() => setConfirmingAccept(null)}
      />

      <IonToast
        isOpen={!!rel.toast}
        message={rel.toast}
        duration={2500}
        onDidDismiss={() => rel.setToast("")}
      />
    </div>
  );
};

/** Self-contained: its own relationship hook. The Data Sharing screen uses the View with the
 *  screen's shared instance instead (see `ShareOrgsSection`). */
const MyOrgRelationshipCards: React.FC<Props> = (props) => {
  const rel = useMyOrgRelationship();
  return <MyOrgRelationshipCardsView {...props} rel={rel} />;
};

export default MyOrgRelationshipCards;
